import React from 'react';

export type MascotState =
  | 'idle'
  | 'wave'
  | 'happy'
  | 'thinking'
  | 'pointing'
  | 'speaking'
  | 'reading'
  | 'encourage'
  | 'celebrate'
  | 'oops';

interface RaraMascotProps {
  state?: MascotState;
  message?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showBubble?: boolean;
  onBubbleClick?: () => void;
}

export const RaraMascot: React.FC<RaraMascotProps> = ({
  state = 'idle',
  message,
  size = 'md',
  className = '',
  showBubble = true,
  onBubbleClick,
}) => {
  const sizeMap = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-36 h-36',
    xl: 'w-48 h-48',
  };

  // Eyes and mouth variation based on semantic state
  const isHappy = state === 'happy' || state === 'celebrate' || state === 'wave';
  const isThinking = state === 'thinking' || state === 'reading';
  const isEncourage = state === 'encourage';
  const isOops = state === 'oops';

  return (
    <div className={`relative flex flex-col items-center select-none ${className}`}>
      {/* Speech Bubble */}
      {showBubble && message && (
        <div
          onClick={onBubbleClick}
          className="mb-2 max-w-xs cursor-pointer bg-white px-4 py-2.5 rounded-2xl shadow-md border-2 border-amber-200 text-[#243047] text-sm md:text-base font-medium flex items-center gap-2 transform transition-all hover:scale-105 active:scale-95 animate-bounce-short"
        >
          <span>{message}</span>
          <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">🔊</span>
        </div>
      )}

      {/* Mascot Illustrated SVG Character */}
      <div className={`relative ${sizeMap[size]} transition-transform duration-300 ${state === 'celebrate' ? 'animate-bounce' : state === 'wave' ? 'animate-pulse' : ''}`}>
        <svg
          viewBox="0 0 160 160"
          className="w-full h-full filter drop-shadow-md"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Gentle Drop Shadow */}
          <ellipse cx="80" cy="148" rx="46" ry="10" fill="#E2E8F0" opacity="0.7" />

          {/* Ears / Head Tuft */}
          <path d="M50 45 C35 25, 52 10, 64 32 Z" fill="#FFB84D" stroke="#E69500" strokeWidth="3" />
          <path d="M110 45 C125 25, 108 10, 96 32 Z" fill="#FFB84D" stroke="#E69500" strokeWidth="3" />
          <path d="M54 39 C46 29, 56 22, 62 34 Z" fill="#FFDCA8" />
          <path d="M106 39 C114 29, 104 22, 98 34 Z" fill="#FFDCA8" />

          {/* Body */}
          <ellipse cx="80" cy="108" rx="44" ry="38" fill="#4F7CF7" stroke="#3662DE" strokeWidth="3" />
          {/* Tummy Belly Patch */}
          <ellipse cx="80" cy="112" rx="28" ry="24" fill="#EAF0FF" />

          {/* Scarf / Signature Accessory */}
          <path
            d="M52 82 Q80 94 108 82 Q112 90 102 96 Q80 102 58 96 Q48 90 52 82 Z"
            fill="#FF5A5F"
            stroke="#D9383D"
            strokeWidth="2.5"
          />
          <path d="M96 90 L108 114 L94 116 Z" fill="#FF5A5F" stroke="#D9383D" strokeWidth="2" />
          {/* Little Star Pin on Scarf */}
          <polygon
            points="99,98 101,102 105,103 102,106 103,110 99,108 95,110 96,106 93,103 97,102"
            fill="#FFD700"
          />

          {/* Head */}
          <ellipse cx="80" cy="62" rx="42" ry="38" fill="#FFB84D" stroke="#E69500" strokeWidth="3" />
          {/* Face Cheeks */}
          <ellipse cx="54" cy="74" rx="8" ry="5" fill="#FF7B92" opacity="0.6" />
          <ellipse cx="106" cy="74" rx="8" ry="5" fill="#FF7B92" opacity="0.6" />

          {/* Eyes */}
          {isHappy ? (
            /* Happy curved smiling eyes */
            <>
              <path d="M60 62 Q68 52 76 62" stroke="#243047" strokeWidth="4.5" strokeLinecap="round" fill="none" />
              <path d="M84 62 Q92 52 100 62" stroke="#243047" strokeWidth="4.5" strokeLinecap="round" fill="none" />
            </>
          ) : isThinking ? (
            /* Thinking curious eyes glancing up */
            <>
              <circle cx="68" cy="58" r="6.5" fill="#243047" />
              <circle cx="92" cy="58" r="6.5" fill="#243047" />
              <circle cx="70" cy="56" r="2.5" fill="#FFFFFF" />
              <circle cx="94" cy="56" r="2.5" fill="#FFFFFF" />
            </>
          ) : isOops ? (
            /* Oops slightly tilted eyes */
            <>
              <circle cx="68" cy="63" r="5" fill="#243047" />
              <circle cx="92" cy="63" r="5" fill="#243047" />
              <ellipse cx="68" cy="54" rx="6" ry="1.5" fill="#243047" transform="rotate(-10 68 54)" />
              <ellipse cx="92" cy="54" rx="6" ry="1.5" fill="#243047" transform="rotate(10 92 54)" />
            </>
          ) : (
            /* Normal wide curious eyes */
            <>
              <ellipse cx="68" cy="62" rx="6.5" ry="8" fill="#243047" />
              <ellipse cx="92" cy="62" rx="6.5" ry="8" fill="#243047" />
              <circle cx="66" cy="59" r="3" fill="#FFFFFF" />
              <circle cx="90" cy="59" r="3" fill="#FFFFFF" />
              <circle cx="70" cy="65" r="1.5" fill="#FFFFFF" />
              <circle cx="94" cy="65" r="1.5" fill="#FFFFFF" />
            </>
          )}

          {/* Cute Nose */}
          <ellipse cx="80" cy="68" rx="3.5" ry="2.5" fill="#884400" />

          {/* Mouth */}
          {isHappy || state === 'speaking' ? (
            <path d="M72 73 Q80 84 88 73 Z" fill="#E53E3E" stroke="#243047" strokeWidth="2.5" />
          ) : isThinking ? (
            <circle cx="80" cy="74" r="3.5" fill="#243047" />
          ) : isEncourage ? (
            <path d="M73 73 Q80 79 87 73" stroke="#243047" strokeWidth="3" strokeLinecap="round" fill="none" />
          ) : isOops ? (
            <ellipse cx="80" cy="75" rx="4" ry="5" fill="#243047" />
          ) : (
            <path d="M73 72 Q80 78 87 72" stroke="#243047" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          )}

          {/* Arms / Hands */}
          {state === 'wave' || state === 'celebrate' ? (
            /* Waving arm */
            <>
              <path d="M38 102 Q22 82 32 68 Q40 68 44 80 Z" fill="#FFB84D" stroke="#E69500" strokeWidth="2.5" />
              <path d="M122 102 Q138 100 126 118 Z" fill="#FFB84D" stroke="#E69500" strokeWidth="2.5" />
            </>
          ) : state === 'pointing' ? (
            /* Pointing arm */
            <>
              <path d="M38 102 Q15 95 18 84 Q28 82 42 94 Z" fill="#FFB84D" stroke="#E69500" strokeWidth="2.5" />
              <path d="M122 102 Q138 108 126 118 Z" fill="#FFB84D" stroke="#E69500" strokeWidth="2.5" />
            </>
          ) : (
            /* Resting arms */
            <>
              <ellipse cx="38" cy="108" rx="9" ry="14" fill="#FFB84D" stroke="#E69500" strokeWidth="2.5" transform="rotate(20 38 108)" />
              <ellipse cx="122" cy="108" rx="9" ry="14" fill="#FFB84D" stroke="#E69500" strokeWidth="2.5" transform="rotate(-20 122 108)" />
            </>
          )}

          {/* Feet */}
          <ellipse cx="64" cy="142" rx="14" ry="8" fill="#FF9A1F" stroke="#E69500" strokeWidth="2" />
          <ellipse cx="96" cy="142" rx="14" ry="8" fill="#FF9A1F" stroke="#E69500" strokeWidth="2" />
        </svg>
      </div>
    </div>
  );
};
